import { useEffect, useMemo, useState } from 'react';

import {
  ColumnDef,
  Icon,
  Pagination,
  RyeconSpinner,
  SortingState,
  Table,
  TextCell,
} from '@dbt-labs/sourdough';

import { CostInsight, Grouping } from '../typings/costInsights';
import { COLUMN_LABELS } from '../util/columnLabels';
import {
  formatCredits,
  formatCurrency,
  formatDateByGrouping,
  formatMinutes,
  formatNumber,
  groupDataByTimeGrouping,
  isRowCostPending,
} from '../util/costInsights';
import { sortTableData } from '../util/tableSorting';

type ChartTableRow = {
  date: Date;
  dateFormatted: string;
  modelBuilds: number;
  modelReused: number;
  runDuration: number; // Display value in minutes
  runDurationFormatted: string; // Formatted string for display
  runDurationSeconds: number; // Always in seconds for sorting
  usage: number;
  usageSaved: number;
  cost: number;
  costSaved: number;
  isCostProcessed: boolean;
};

type ChartTableProps = {
  data: CostInsight[];
  grouping: Grouping;
  isLoading?: boolean;
  isPreparing?: boolean;
  testId?: string;
  /** When provided, the -- placeholder is only shown if the cron also ran recently (within 48h). */
  connectionTestUpdatedAt?: Date;
};

const DEFAULT_ROWS_PER_PAGE = 10;

export const ChartTable = ({
  data,
  grouping,
  isLoading = false,
  isPreparing = false,
  testId = 'cost-insights-table',
  connectionTestUpdatedAt,
}: ChartTableProps) => {
  const [sortState, setSortState] = useState<SortingState>([
    { id: 'date', desc: true },
  ]);
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = DEFAULT_ROWS_PER_PAGE;

  // Reset to first page when data or grouping changes
  useEffect(() => {
    setCurrentPage(0);
  }, [data, grouping]);

  // Group the data based on the grouping
  const groupedData = useMemo(() => {
    return groupDataByTimeGrouping(data, grouping);
  }, [data, grouping]);

  // Transform data into table rows with formatted dates
  const tableRows = useMemo<ChartTableRow[]>(() => {
    return groupedData.map((item) => {
      const runDurationMinutes = (item.executionTime || 0) / 60;
      return {
        date: item.date,
        dateFormatted: formatDateByGrouping(item.date, grouping),
        modelBuilds: item.executionCount,
        modelReused: item.reusedCount,
        runDuration: runDurationMinutes, // Convert to minutes
        runDurationFormatted: formatMinutes(runDurationMinutes),
        runDurationSeconds: item.executionTime || 0,
        usage: item.executionComputeUnits,
        usageSaved: item.executionComputeUnitsSaved,
        cost: item.executionCost,
        costSaved: item.executionCostSaved,
        isCostProcessed: item.isCostProcessed,
      };
    });
  }, [groupedData, grouping]);

  // Sort the data
  const sortedData = useMemo(() => {
    if (sortState.length === 0) return tableRows;

    const sort = sortState[0];
    const sortKey = sort.id as keyof ChartTableRow;
    const isDescending = sort.desc ?? false;

    // Determine column type for sorting
    let columnType: 'number' | 'string' | 'date' = 'string';
    let actualSortKey: keyof ChartTableRow = sortKey;

    if (sortKey === 'date') {
      columnType = 'date';
    } else if (sortKey === 'runDuration') {
      // Use the numeric runDurationSeconds for proper sorting
      columnType = 'number';
      actualSortKey = 'runDurationSeconds';
    } else {
      columnType = 'number';
    }

    return sortTableData(tableRows, actualSortKey, isDescending, columnType);
  }, [tableRows, sortState]);

  // Paginate the sorted data
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, rowsPerPage]);

  const columns: ColumnDef<ChartTableRow>[] = useMemo(() => {
    const isPending = (row: ChartTableRow) =>
      isRowCostPending({
        grouping,
        rowDate: row.date,
        isCostProcessed: row.isCostProcessed,
        connectionTestUpdatedAt,
      });

    return [
      {
        header: COLUMN_LABELS.date,
        accessorKey: 'date',
        size: 200,
        id: 'date',
        cell: ({ row }) => (
          <TextCell
            title={{
              text: row.original.dateFormatted,
            }}
          />
        ),
      },
      {
        header: COLUMN_LABELS.modelBuilds,
        accessorKey: 'modelBuilds',
        size: 120,
        id: 'modelBuilds',
        meta: {
          align: 'right',
        },
        cell: ({ getValue }) => (
          <TextCell
            title={{
              text: formatNumber(getValue<number>()),
            }}
          />
        ),
      },
      {
        header: COLUMN_LABELS.modelReused,
        accessorKey: 'modelReused',
        size: 120,
        id: 'modelReused',
        meta: {
          align: 'right',
        },
        cell: ({ getValue }) => (
          <TextCell
            title={{
              text: formatNumber(getValue<number>()),
            }}
          />
        ),
      },
      {
        header: COLUMN_LABELS.runDuration,
        accessorKey: 'runDuration',
        size: 120,
        id: 'runDuration',
        meta: {
          align: 'right',
        },
        cell: ({ row }) => (
          <TextCell
            title={{
              text: isPending(row.original) ? '--' : row.original.runDurationFormatted,
            }}
          />
        ),
      },
      {
        header: COLUMN_LABELS.computeExecuted,
        accessorKey: 'usage',
        size: 120,
        id: 'usage',
        meta: {
          align: 'right',
        },
        cell: ({ row }) => (
          <TextCell
            title={{
              text: isPending(row.original) ? '--' : formatCredits(row.original.usage),
            }}
          />
        ),
      },
      {
        header: COLUMN_LABELS.computeAvoided,
        accessorKey: 'usageSaved',
        size: 120,
        id: 'usageSaved',
        meta: {
          align: 'right',
        },
        cell: ({ row }) => (
          <TextCell
            title={{
              text: isPending(row.original)
                ? '--'
                : formatCredits(row.original.usageSaved),
              className: 'text-fgSuccess',
            }}
          />
        ),
      },
      {
        header: COLUMN_LABELS.cost,
        accessorKey: 'cost',
        size: 120,
        id: 'cost',
        meta: {
          align: 'right',
        },
        cell: ({ row }) => (
          <TextCell
            title={{
              text: isPending(row.original) ? '--' : formatCurrency(row.original.cost),
            }}
          />
        ),
      },
      {
        header: COLUMN_LABELS.costSaved,
        accessorKey: 'costSaved',
        size: 120,
        id: 'costSaved',
        meta: {
          align: 'right',
        },
        cell: ({ row }) => (
          <TextCell
            title={{
              text: isPending(row.original)
                ? '--'
                : formatCurrency(row.original.costSaved),
              className: 'text-fgSuccess',
            }}
          />
        ),
      },
    ];
  }, [connectionTestUpdatedAt, grouping]);

  return (
    <div className="flex flex-col gap-4">
      <div
        className="max-w-full overflow-x-auto [&_table]:!bg-transparent [&_tbody]:!bg-transparent [&_thead]:!bg-transparent [&_tr:not(:hover)>*]:!bg-transparent [&_tr:not(:hover)]:!bg-transparent"
        // The background styles below create a fade effect on the left and right edges of the table to indicate that there is more content to scroll to. The radial gradients add a subtle shadow effect to enhance the visual cue.
        style={{
          background: [
            'linear-gradient(to right, white 30%, rgba(255, 255, 255, 0)) left center',
            'linear-gradient(to left, white 30%, rgba(255, 255, 255, 0)) right center',
            'radial-gradient(farthest-side at 0% 50%, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) left center',
            'radial-gradient(farthest-side at 100% 50%, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) right center',
          ].join(', '),
          backgroundColor: 'white',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '40px 100%, 40px 100%, 14px 100%, 14px 100%',
          backgroundAttachment: 'local, local, scroll, scroll',
        }}
      >
        <div className={isPreparing ? '[&_tbody]:hidden' : undefined}>
          <Table
            className="!overflow-visible"
            testId={testId}
            isLoading={isLoading}
            data={isPreparing ? [] : paginatedData}
            columns={columns}
            isSortable={!isPreparing}
            initialSortColumn="date"
            initialSortDesc={true}
            onChangeSort={setSortState}
          />
        </div>
        {isPreparing && (
          <div className="flex flex-col items-center justify-center gap-2 py-6 text-fgAlt">
            <Icon ryecon={RyeconSpinner} className="animate-spin" alt="Loading" />
            Preparing cost insights...
          </div>
        )}
      </div>
      {!isPreparing && (
        <Pagination
          onPageChange={setCurrentPage}
          totalRows={sortedData.length}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
        />
      )}
    </div>
  );
};
